
import { useState, useEffect, useMemo, useRef } from 'react';
import { FailureFilters } from '@/types/filters';
import { WeibullAnalysisResult } from '@/utils/weibull';
import { createLogger } from '@/utils/logger';
import {
  ForecastResult,
  ForecastConfig,
  SimulationMetadata,
  TurbineForForecast
} from '@/types/forecast';
import { useFailures } from '@/hooks/useFailures';
import { FailureRecord } from '@/types/failure';
import { PlannedFailuresByPeriod } from '@/types/workerTypes';
import { FORECAST_CALCULATION_CONSTANTS } from '@/constants/forecastCalculation';
import { usePopulationAnalysis } from '@/hooks/usePopulationAnalysis';
import { useValidationErrors } from '@/hooks/useValidationErrors';
import { aggregatePlannedFailures } from '@/utils/forecastCalculationHelpers';
// @ts-expect-error - Worker import without types
import ForecastWorker from '../workers/forecast.worker';


interface UseForecastCalculationProps {
  sourceAnalysis: WeibullAnalysisResult | null;
  filters: FailureFilters;
  configuration: ForecastConfig;
  enabled?: boolean;
}

interface ForecastCalculationResult {
  // Results
  forecastResults: ForecastResult[];
  populationSize: number;
  forecastSubsetSize: number;
  simulationMetadata: SimulationMetadata | null;

  // Loading states
  isCalculating: boolean;
  turbinesLoading: boolean;
  calculationProgress: number;
  progressText: string;

  // Validation
  canCalculate: boolean;
  validationErrors: string[];

  // Actions
  calculateForecast: () => Promise<void>;
  clearResults: () => void;
  setForecastResults: (results: ForecastResult[], metadata?: SimulationMetadata) => void;

  // Debug info
  _turbineData?: FailureRecord[];
  _turbinesForForecast?: TurbineForForecast[];
}

const logger = createLogger('ForecastWorker');

export const useForecastCalculationWebWorker = ({
  sourceAnalysis,
  filters,
  configuration
}: UseForecastCalculationProps): ForecastCalculationResult => {
  const [forecastResults, setForecastResults] = useState<ForecastResult[]>([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [simulationMetadata, setSimulationMetadata] = useState<SimulationMetadata | null>(null);

  const [realProgress, setRealProgress] = useState(0);
  const [displayProgress, setDisplayProgress] = useState(0);
  const [progressText, setProgressText] = useState('');

  const workerRef = useRef<Worker | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const turbineFilters = {
    ...filters,
    size: FORECAST_CALCULATION_CONSTANTS.TURBINE_FETCH_SIZE,
    page: 1,
    excludedCaseIds: []
  };

  const {
    data: turbineData,
    isLoading: turbinesLoading
  } = useFailures(turbineFilters);

  useEffect(() => {
    if (!isCalculating) {
      setDisplayProgress(0);
      setRealProgress(0);
      return;
    }

    const animate = () => {
      setDisplayProgress(prev => {
        const diff = realProgress - prev;
        const step = diff * 0.15;

        if (Math.abs(diff) < 0.1) {
          return realProgress;
        }

        return prev + step;
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isCalculating, realProgress]);

  // Population analysis using custom hook
  const { populationSize, forecastSubsetSize, turbinesForForecast, validationErrors: populationErrors } = usePopulationAnalysis({
    turbineData,
    sourceAnalysis,
    filters
  });

  // Validation errors using custom hook
  const { validationErrors } = useValidationErrors(populationErrors);

  // Calculate if we can perform forecast
  const canCalculate = useMemo(() => {
    return !!
      sourceAnalysis &&
      populationSize > 0 &&
      populationErrors.length === 0 &&
      !turbinesLoading &&
      !isCalculating;
  }, [sourceAnalysis, populationSize, populationErrors, turbinesLoading, isCalculating]);

  const calculateForecast = async () => {
    if (!canCalculate || !sourceAnalysis || populationSize === 0) {
      logger.warn('Cannot calculate forecast: validation failed');
      return;
    }

    setIsCalculating(true);
    setRealProgress(0);
    setDisplayProgress(0);
    setProgressText('Analyzing failure patterns...');
    setSimulationMetadata(null);

    try {
      // Aggregate planned failures if enabled
      let plannedFailures: PlannedFailuresByPeriod | undefined;

      if (configuration.includePlannedFailures && turbineData?.failures) {
        plannedFailures = aggregatePlannedFailures(
          turbineData.failures,
          filters,
          configuration.horizonYears,
          configuration.granularityMonths,
          configuration.startDate
        );
      }

      // Create Web Worker using worker-loader
      const worker = new ForecastWorker();
      workerRef.current = worker;

      // Set up message handling
      worker.addEventListener('message', (event) => {
        if (event.origin !== '' || !event.isTrusted) {
          return;
        }
        const { type, progress, stage, error: _error, details } = event.data;

        if (type === 'pong') {
          // Continue with actual calculation after ping test succeeds
          return;
        } else if (type === 'progress') {
          setRealProgress(progress || 0);
          setProgressText(stage || '');
        } else if (type === 'complete') {
          const { results: _results, metadata } = event.data;


          // Use metadata from worker
          setSimulationMetadata(metadata);
          setForecastResults(_results);
          setIsCalculating(false);

          // Cleanup
          worker.terminate();
        } else if (type === 'error') {
          if (details) {
            logger.error('Error details:', details);
          }
          setIsCalculating(false);
          worker.terminate();
        }
      });

      // Add error event listener for worker errors
      worker.addEventListener('error', () => {
        setIsCalculating(false);
      });

      // Add timeout detection
      const timeoutId = setTimeout(() => {
        if (isCalculating) {
          setIsCalculating(false);
          worker.terminate();
        }
      }, FORECAST_CALCULATION_CONSTANTS.WORKER_TIMEOUT_MS);

      // Clear timeout when worker completes or errors
      const originalTerminate = worker.terminate.bind(worker);
      worker.terminate = () => {
        clearTimeout(timeoutId);
        originalTerminate();
      };

      worker.postMessage({ type: 'ping' });


      // Send calculation request to worker
      worker.postMessage({
        type: 'calculate',
        payload: {
          turbinesForForecast,
          weibullParameters: sourceAnalysis.parameters,
          horizonYears: configuration.horizonYears,
          granularityMonths: configuration.granularityMonths,
          startDate: configuration.startDate,
          useMonteCarloSimulation: configuration.useMonteCarloSimulation,
          plannedFailures, // Add planned failures
          simulationOptions: {
            parameterSamples: configuration.parameterSamples || 30,
            trajectoriesPerParameter: configuration.trajectoriesPerParameter || 50,
            maxSimulations: (configuration.parameterSamples || 500) * (configuration.trajectoriesPerParameter || 50),
            minSimulations: Math.min(2000, (configuration.parameterSamples || 30) * (configuration.trajectoriesPerParameter || 50)),
            convergenceThreshold: 0.01
          },
          mleResult: sourceAnalysis.mleResult,
          confidenceOptions: {
            confidenceLevel: configuration.confidenceLevel,
            includeConfidenceIntervals: configuration.includeConfidenceIntervals
          },
          seed: Date.now() // Add seed for reproducibility
        }
      });

    } catch (err) {
      logger.error('Failed to start Web Worker:', err);
      setIsCalculating(false);
      setRealProgress(0);
      setProgressText('');
    }
  };

  const clearResults = () => {
    setForecastResults([]);
    setSimulationMetadata(null);
    setRealProgress(0);
    setDisplayProgress(0);
    setProgressText('');

    // Terminate any running worker
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }
  };

  const setForecastResultsWithMetadata = (results: ForecastResult[], metadata?: SimulationMetadata) => {
    setForecastResults(results);
    if (metadata) {
      setSimulationMetadata(metadata);
    }
    // Clear any calculation state when setting pre-calculated results
    setIsCalculating(false);
    setRealProgress(100);
    setDisplayProgress(100);
    setProgressText('Forecast loaded');

    // Terminate any running worker since we're loading pre-calculated results
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (workerRef.current) {
        workerRef.current.terminate();
      }
    };
  }, []);

  return {
    // Results
    forecastResults,
    populationSize,
    forecastSubsetSize,
    simulationMetadata,

    // Loading states
    isCalculating,
    turbinesLoading,
    calculationProgress: displayProgress, // Use smoothly animated progress
    progressText,

    // Validation
    canCalculate,
    validationErrors,

    // Actions
    calculateForecast,
    clearResults,
    setForecastResults: setForecastResultsWithMetadata,

    // Debug info
    _turbineData: turbineData?.failures,
    _turbinesForForecast: turbinesForForecast.slice(0, 5)
  };
};
